/**
 * citationMarkers — the marker grammar, in one place.
 *
 * The engine canonicalizes markers before they reach us: a single `[3]` or a
 * group `[3,7,12]` — ascending, comma-separated, no spaces. It never emits
 * `[3][7]` or `[3, 7]`. This parser is deliberately a little more forgiving
 * about spaces than the contract requires (older stored blobs have them), but
 * it produces exactly one chip PER NUMBER either way.
 */

import {
  CitationLocator,
  CitationSection,
  LocatorStrategy,
  ResearchCitation,
} from './types';

/** The contract's regex. */
export const MARKER_PATTERN = /\[(\d+(?:,\d+)*)\]/g;
/** What we actually scan with — the same, tolerating stored whitespace. */
const TOLERANT = /\[(\d+(?:\s*,\s*\d+)*)\]/g;

export interface MarkerSegment {
  /** Plain text, or a marker to render as chips. */
  kind: 'text' | 'marker';
  text: string;
  /** For a marker: the numbers it names, in the order written. */
  numbers: number[];
}

/**
 * Split text into plain runs and markers.
 *
 * A marker whose numbers resolve to no citation is left as LITERAL TEXT — a
 * reference silently vanishing from an answer is worse than an unlinked one.
 */
export function parseMarkers(text: string, known?: (n: number) => boolean): MarkerSegment[] {
  if (!text) return [];

  const segments: MarkerSegment[] = [];
  const re = new RegExp(TOLERANT);
  let cursor = 0;
  let match: RegExpExecArray | null;

  // eslint-disable-next-line no-cond-assign
  while ((match = re.exec(text)) !== null) {
    const numbers = match[1]
      .split(',')
      .map((n) => Number(n.trim()))
      .filter((n) => Number.isFinite(n));
    const resolvable = known ? numbers.filter(known) : numbers;

    if (resolvable.length === 0) continue;

    if (match.index > cursor) {
      segments.push({ kind: 'text', text: text.slice(cursor, match.index), numbers: [] });
    }
    segments.push({ kind: 'marker', text: match[0], numbers: resolvable });
    cursor = match.index + match[0].length;
  }

  if (cursor < text.length) {
    segments.push({ kind: 'text', text: text.slice(cursor), numbers: [] });
  }
  return segments;
}

/** Every citation number referenced by a body of text, in order of appearance. */
export function markerNumbers(text: string): number[] {
  const seen: number[] = [];
  parseMarkers(text).forEach((segment) => {
    segment.numbers.forEach((n) => {
      if (!seen.includes(n)) seen.push(n);
    });
  });
  return seen;
}

/* ------------------------------------------------------------------ */
/*  Reaching the cited passage                                         */
/* ------------------------------------------------------------------ */

export type LocatorTarget =
  | { kind: 'text_fragment'; url: string }
  | { kind: 'quote'; quote: string }
  | { kind: 'char_range'; start: number; end: number }
  | { kind: 'heading'; heading: string }
  | { kind: 'chunk'; chunk: number }
  | { kind: 'top' };

/**
 * How to reach the passage, strongest usable form first.
 *
 * `strategy` names what the engine considers strongest, so the walk STARTS
 * there and falls through — a browser without text-fragment support, or a
 * converted document whose offsets do not match the rendered text, has to
 * land somewhere sensible rather than nowhere.
 *
 * `char_range` is skipped when precision is `unavailable`: offsets that do
 * not correspond to the rendered document would scroll to the wrong place,
 * which is worse than scrolling to the top.
 */
export function resolveLocator(
  locator: CitationLocator | null | undefined,
  url = ''
): LocatorTarget {
  if (!locator) return { kind: 'top' };

  const order: LocatorStrategy[] = ['text_fragment', 'quote', 'char_range', 'heading', 'chunk'];
  const from = order.indexOf(locator.strategy);
  const walk = from >= 0 ? order.slice(from) : order;

  for (const step of walk) {
    if (step === 'text_fragment' && locator.text_fragment && url) {
      const fragment = locator.text_fragment.replace(/^#/, '');
      return { kind: 'text_fragment', url: `${url.split('#')[0]}#${fragment}` };
    }
    if (step === 'quote' && locator.quote.trim()) {
      return { kind: 'quote', quote: locator.quote.trim() };
    }
    if (
      step === 'char_range' &&
      locator.precision !== 'unavailable' &&
      typeof locator.start_char === 'number' &&
      typeof locator.end_char === 'number' &&
      locator.end_char > locator.start_char
    ) {
      return { kind: 'char_range', start: locator.start_char, end: locator.end_char };
    }
    if (step === 'heading' && locator.heading.trim()) {
      return { kind: 'heading', heading: locator.heading.trim() };
    }
    if (step === 'chunk' && typeof locator.chunk === 'number' && locator.chunk >= 0) {
      return { kind: 'chunk', chunk: locator.chunk };
    }
  }

  return { kind: 'top' };
}

/**
 * A locator aimed at ONE section of the document.
 *
 * Built from that section's own fields so a jump target lands on its own
 * passage rather than the citation's primary one. `char_range` is offered
 * only when the section's precision allows it — the same rule the top-level
 * locator follows, applied per section because precision varies between them.
 */
export function sectionLocator(section: CitationSection): CitationLocator {
  const usableOffsets =
    section.precision !== 'unavailable' &&
    typeof section.start_char === 'number' &&
    typeof section.end_char === 'number' &&
    section.end_char > section.start_char;

  const strategy: LocatorStrategy =
    section.excerpt.trim() ? 'quote'
      : usableOffsets ? 'char_range'
      : section.heading.trim() ? 'heading'
      : section.chunk_index !== null ? 'chunk'
      : 'none';

  return {
    strategy,
    // A section never carries a text fragment; the excerpt is the quote.
    text_fragment: '',
    quote: section.excerpt.trim(),
    start_char: section.start_char,
    end_char: section.end_char,
    precision: section.precision,
    heading: section.heading.trim(),
    chunk: section.chunk_index,
    sections: [],
  };
}

/**
 * The passages worth offering as jump targets.
 *
 * One section is just the citation — the primary click already goes there, so
 * a list of one would be noise. Anything more is a genuine choice.
 */
export function jumpSections(citation: ResearchCitation): CitationSection[] {
  const sections = citation.locator?.sections || [];
  return sections.length > 1 ? sections : [];
}

/** A short label for a jump target. */
export function sectionLabel(section: CitationSection, index: number): string {
  const heading = section.heading.trim();
  if (heading) return heading;
  const excerpt = section.excerpt.trim();
  if (excerpt) return excerpt.length > 80 ? `${excerpt.slice(0, 77)}…` : excerpt;
  return `Passage ${index + 1}`;
}

/**
 * Every way to reach the passage, best first.
 *
 * `resolveLocator` answers "where should this land?" with ONE target, walking
 * down from the strategy the engine named. That is not enough on real
 * documents: the walk starts AT the named strategy, so a citation whose
 * strategy is `char_range` skipped the quote entirely — and offsets are
 * exactly the thing that goes wrong. Merged chunks routinely leave
 * `start_char`/`end_char` at 0, and a converted document's rendered text does
 * not line up with the offsets taken over the extracted text anyway.
 *
 * So the viewer gets the whole chain and tries each in turn. The quote is
 * ALWAYS in it when there is one — it is the only form that survives
 * conversion, because it is searched for in the text the reader can see.
 *
 * `fallbackQuote` is the citation's own verbatim passage, used when the
 * locator carries none of its own.
 */
export function locatorTargets(
  locator: CitationLocator | null | undefined,
  url = '',
  fallbackQuote = ''
): LocatorTarget[] {
  const quote = (locator?.quote || '').trim() || fallbackQuote.trim();

  const available = new Map<LocatorStrategy, LocatorTarget>();
  if (locator?.text_fragment && url) {
    available.set('text_fragment', {
      kind: 'text_fragment',
      url: `${url.split('#')[0]}#${locator.text_fragment.replace(/^#/, '')}`,
    });
  }
  if (quote) available.set('quote', { kind: 'quote', quote });
  if (
    locator &&
    locator.precision !== 'unavailable' &&
    typeof locator.start_char === 'number' &&
    typeof locator.end_char === 'number' &&
    locator.end_char > locator.start_char
  ) {
    available.set('char_range', {
      kind: 'char_range',
      start: locator.start_char,
      end: locator.end_char,
    });
  }
  if (locator?.heading.trim()) {
    available.set('heading', { kind: 'heading', heading: locator.heading.trim() });
  }
  if (locator && typeof locator.chunk === 'number' && locator.chunk >= 0) {
    available.set('chunk', { kind: 'chunk', chunk: locator.chunk });
  }

  const canonical: LocatorStrategy[] =
    ['text_fragment', 'quote', 'char_range', 'heading', 'chunk'];
  // The engine's own preference leads; everything else still gets a turn.
  const order = locator && available.has(locator.strategy)
    ? [locator.strategy, ...canonical.filter((s) => s !== locator.strategy)]
    : canonical;

  const targets = order
    .map((step) => available.get(step))
    .filter((target): target is LocatorTarget => !!target);

  // Opening at the top is a real outcome, not a failure: a document we could
  // not aim at still opens.
  return [...targets, { kind: 'top' }];
}

/* ------------------------------------------------------------------ */
/*  Finding and marking the passage in the rendered document           */
/* ------------------------------------------------------------------ */

/** The class the located passage carries while the source pane is open. */
export const PASSAGE_HIGHLIGHT_CLASS = 'pons-cited-passage';
const HIGHLIGHT_STYLE_ID = 'pons-cited-passage-style';

/**
 * Champagne, literally.
 *
 * The framed document is its own document — it cannot see the app's CSS
 * variables, so the brand accent is written out here rather than referenced.
 * `--champagne: 216 185 106` in `src/index.css` is the single source; this
 * must move with it.
 */
const HIGHLIGHT_CSS = `
.${PASSAGE_HIGHLIGHT_CLASS} {
  background-color: rgba(216, 185, 106, 0.28) !important;
  box-shadow: 0 0 0 2px rgba(216, 185, 106, 0.35);
  border-radius: 3px;
  scroll-margin: 4rem;
  transition: background-color 400ms ease, box-shadow 400ms ease;
}
`;

/** Make sure the highlight rule exists in whichever document we are marking. */
function ensureHighlightStyle(doc: Document): void {
  if (!doc || doc.getElementById(HIGHLIGHT_STYLE_ID)) return;
  const style = doc.createElement('style');
  style.id = HIGHLIGHT_STYLE_ID;
  style.textContent = HIGHLIGHT_CSS;
  (doc.head || doc.body || doc.documentElement)?.appendChild(style);
}

/** Drop any highlight left over from a previously opened citation. */
export function clearHighlight(root: Document | HTMLElement | null | undefined): void {
  if (!root) return;
  Array.from(root.querySelectorAll(`.${PASSAGE_HIGHLIGHT_CLASS}`)).forEach((node) =>
    node.classList.remove(PASSAGE_HIGHLIGHT_CLASS)
  );
}

/**
 * Find a passage in a rendered document.
 *
 * A text walk rather than `Range`/`find`: converted documents split a
 * sentence across many absolutely-positioned nodes, so matching whole text
 * nodes is unreliable. Matching progressively shorter prefixes is what makes
 * this land on real-world converter output — and it works in every browser,
 * which text fragments do not.
 *
 * The SMALLEST element containing the probe wins. Without that, the first
 * match in document order is nearly always a wrapper `<div>` spanning the
 * whole page, and highlighting the page is not guiding anyone anywhere.
 */
export function findPassage(
  root: Document | HTMLElement,
  needle: string
): HTMLElement | null {
  const wanted = needle.trim().toLowerCase();
  const scope: ParentNode | null =
    (root as Document).body !== undefined ? (root as Document).body : (root as HTMLElement);
  if (!wanted || !scope) return null;

  const candidates = Array.from(
    scope.querySelectorAll<HTMLElement>('p,div,span,td,li,h1,h2,h3,h4,h5,h6,blockquote,pre')
  );
  // Longest prefix first: an exact hit is better than a loose one.
  const probes = [wanted, wanted.slice(0, 120), wanted.slice(0, 60), wanted.slice(0, 30)]
    .map((p) => p.trim())
    .filter((p) => p.length >= 12);

  for (const probe of probes) {
    const hits = candidates.filter((node) =>
      (node.textContent || '').toLowerCase().includes(probe)
    );
    if (hits.length === 0) continue;
    // Tightest wrapper around the passage — never the page it sits on.
    return hits.reduce((best, node) => (best.contains(node) ? node : best), hits[0]);
  }
  return null;
}

/**
 * Land on a passage: scroll it into view and mark it.
 *
 * The highlight is what makes this "guide me to the section" rather than
 * "open the document somewhere near it" — a converted page is a wall of
 * identical text, and a scroll position alone says nothing about which
 * sentence was cited.
 */
export function revealPassage(root: Document | HTMLElement, node: HTMLElement): void {
  const doc = node.ownerDocument;
  if (doc) ensureHighlightStyle(doc);
  clearHighlight(root);
  node.classList.add(PASSAGE_HIGHLIGHT_CLASS);
  node.scrollIntoView({ block: 'center' });
}

/** Find a passage and land on it. `false` when it is not in this document. */
export function findAndScroll(root: Document | HTMLElement, needle: string): boolean {
  const hit = findPassage(root, needle);
  if (!hit) return false;
  revealPassage(root, hit);
  return true;
}

/* ------------------------------------------------------------------ */
/*  Opening the source                                                 */
/* ------------------------------------------------------------------ */

/**
 * Is this something a browser can actually navigate to?
 *
 * ABSOLUTE http(s) ONLY, and that is the whole point. A datasource citation
 * carries `url: ""` by design — a tenant's own file has no public address —
 * and what it does carry is a `blob_path` like
 * `firm_x/datasources/y/report.docx`. Anything that treated "ends in .docx" as
 * a link handed that path to an <a href> or an <iframe src>, the browser
 * resolved it against the app's own origin, and the user got
 * `localhost:3000/firm_x/...`. A stored path is never a link; it is a thing to
 * fetch from the stream-source endpoint.
 */
export function isExternalUrl(value: unknown): boolean {
  return /^https?:\/\//i.test(String(value ?? '').trim());
}

export interface CitationTarget {
  /** Stream from storage when there is a path. */
  blobPath: string;
  container: string;
  /** Otherwise open the URL. */
  url: string;
  canStream: boolean;
}

/**
 * Where a citation's document lives.
 *
 * `blob_path` + `container` is the stored copy; an empty container means the
 * default one. With no path at all the `url` is the only way there.
 */
export function citationTarget(citation: ResearchCitation): CitationTarget {
  const blobPath = String(citation.blob_path || '').trim();
  return {
    blobPath,
    container: String(citation.container || '').trim(),
    url: String(citation.url || '').trim(),
    canStream: blobPath !== '',
  };
}

/** A one-line label for a reference entry. */
export function citationHeadline(citation: ResearchCitation): string {
  return (
    citation.title ||
    citation.document_number ||
    citation.file_name ||
    citation.law ||
    `${citation.marker || `[${citation.number}]`}`
  );
}

/** The metadata line under a reference — only the parts that exist. */
export function citationMeta(citation: ResearchCitation): string[] {
  return [
    citation.document_type,
    citation.authority,
    citation.jurisdiction,
    citation.date,
    citation.pinpoint,
  ]
    .map((v) => String(v || '').trim())
    .filter(Boolean);
}
